/**
 * ContactsTable - Tabla de contactos estilo ToDoList
 * Con ordenamiento por columnas clickeables e infinite scroll
 */

import { useState, useMemo, useRef, useEffect, memo, useCallback } from 'react'
import { format, formatDistanceToNow, isPast } from 'date-fns'
import { es } from 'date-fns/locale'
import ContactAvatar from '../shared/ContactAvatar'
import { getDateUrgency } from '../../utils/dateUtils'
import useCRMStore from '../../store/useCRMStore'
import { ALL_COLUMNS } from './columnConfig'
import IconHistoryTooltip from '../shared/IconHistoryTooltip'
import ScarBadge from '../shared/ScarBadge'
import IconSelectorModal from '../shared/IconSelectorModal'
import IconFilterModal from '../shared/IconFilterModal'
import FlagFilterModal from '../shared/FlagFilterModal'
import { useFlipDropdown } from '../../hooks/useFlipDropdown'
import { DescriptionPopover } from '../shared/Tooltip'
import { ICON_DESCRIPTIONS } from '../../utils/iconDescriptions'
import { FLAG_OPTIONS, FLAG_EMOJI, FLAG_LABEL } from '../../utils/flagOptions'
import { NOMBRE_MAP } from '../shared/RatingIcon'
import { colorForAgent } from '../../utils/agentColors'

// Bandera chilena como SVG inline: en Windows+Chrome el emoji 🇨🇱 se renderiza como
// las letras "CL" (los agentes usan Windows), el SVG se ve idéntico en todos los SO.
const ChileFlagIcon = () => (
  <svg width="20" height="14" viewBox="0 0 24 16" className="rounded-[2px]" aria-hidden="true">
    <rect width="24" height="8" fill="#ffffff" />
    <rect y="8" width="24" height="8" fill="#d52b1e" />
    <rect width="8" height="8" fill="#0039a6" />
    <path d="M4 1.7 L4.59 3.39 L6.38 3.43 L4.95 4.51 L5.47 6.22 L4 5.2 L2.53 6.22 L3.05 4.51 L1.62 3.43 L3.41 3.39 Z" fill="#ffffff" />
  </svg>
)

/**
 * LabelBadges - Muestra etiquetas de Wassenger como iconos con tooltip
 * Similar a como lo hace Wassenger en su interfaz
 */
const LabelBadges = memo(function LabelBadges({ labels = [] }) {
  const [showTooltip, setShowTooltip] = useState(false)

  // Filtrar labels válidos (que tengan al menos id)
  const validLabels = labels.filter(l => l && l.id)

  if (validLabels.length === 0) return null

  return (
    <div
      className="relative inline-flex items-center gap-0.5"
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
    >
      {/* Iconos de etiquetas (máximo 3 visibles) */}
      {validLabels.slice(0, 3).map((label, idx) => (
        <span
          key={label.id || idx}
          className="w-3 h-3 rounded-full border border-white shadow-sm"
          style={{ backgroundColor: label.color || '#6B7280' }}
          title={label.name || label.id}
        />
      ))}

      {/* Indicador de más etiquetas */}
      {validLabels.length > 3 && (
        <span className="text-xs text-gray-500 ml-0.5">
          +{validLabels.length - 3}
        </span>
      )}

      {/* Tooltip con nombres completos - posicionado a la izquierda */}
      {showTooltip && (
        <div className="absolute bottom-full right-0 mb-1 z-50 bg-gray-900 text-white text-xs rounded-lg py-2 px-3 shadow-lg whitespace-nowrap">
          <div className="font-medium mb-1 text-gray-300">Etiquetas:</div>
          {validLabels.map((label, idx) => (
            <div key={label.id || idx} className="flex items-center gap-2 py-0.5">
              <span
                className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                style={{ backgroundColor: label.color || '#6B7280' }}
              />
              <span>{label.name || label.id}</span>
            </div>
          ))}
          {/* Flecha del tooltip */}
          <div className="absolute top-full right-3 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900" />
        </div>
      )}
    </div>
  )
})

/**
 * CampaignBadges - Muestra campañas completadas con tooltip detallado
 */
const CampaignBadges = memo(function CampaignBadges({ campaigns = [] }) {
  const [showTooltip, setShowTooltip] = useState(false)
  const [tooltipPos, setTooltipPos] = useState({ top: 0, left: 0 })
  const triggerRef = useRef(null)

  // Filtrar campañas válidas
  const validCampaigns = campaigns.filter(c => c && c.campaign_id)

  if (validCampaigns.length === 0) return null

  // Formatear fecha
  const formatDate = (dateStr) => {
    if (!dateStr) return ''
    try {
      // Extraer solo la parte de fecha para evitar problemas de timezone
      const dateOnly = dateStr.split('T')[0]
      const [year, month, day] = dateOnly.split('-')
      return `${day}/${month}/${year}`
    } catch {
      return ''
    }
  }

  return (
    <div
      ref={triggerRef} className="relative inline-flex items-center"
      onMouseEnter={() => { if (triggerRef.current) { const r = triggerRef.current.getBoundingClientRect(); setTooltipPos({ top: r.top - 8, left: r.right - 16 }); } setShowTooltip(true); }}
      onMouseLeave={() => setShowTooltip(false)}
    >
      {/* Icono de campaña con contador */}
      <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-green-100 text-green-700 text-xs font-medium cursor-default">
        <span>📧</span>
        <span>{validCampaigns.length}</span>
      </span>

      {/* Tooltip con detalle de campañas */}
      {showTooltip && (
        <div className="fixed z-[9999] bg-gray-900 text-white text-xs rounded-lg py-2 px-3 shadow-lg min-w-48" style={{ top: tooltipPos.top + 'px', left: tooltipPos.left + 'px', transform: 'translate(-100%, -100%)' }}>
          <div className="font-medium mb-2 text-green-400 flex items-center gap-1">
            <span>📧</span>
            <span>Campañas Completadas ({validCampaigns.length})</span>
          </div>
          <div className="space-y-2">
            {validCampaigns.map((campaign, idx) => (
              <div key={campaign.id || idx} className="border-t border-gray-700 pt-1.5 first:border-0 first:pt-0">
                <div className="flex items-center gap-1.5">
                  <span className="text-green-400">✓</span>
                  <span className="font-medium">{campaign.campaign_name}</span>
                </div>
                <div className="text-gray-400 ml-4 text-[10px]">
                  {campaign.steps_completed}/{campaign.total_steps} pasos • {formatDate(campaign.completed_at)}
                </div>
              </div>
            ))}
          </div>
          {/* Flecha del tooltip */}
          <div className="hidden" />
        </div>
      )}
    </div>
  )
})

// Configuración de columnas ordenables
// FLAG_OPTIONS / FLAG_EMOJI / FLAG_LABEL: catalogo canonico UNICO en utils/flagOptions.jsx (importado arriba).
// Los render especiales (whatsapp/wassenger SVG, cotizacion_enviada, reciclado_hoy) se manejan inline por key mas abajo.

// Render del flag "cotización enviada": papiro 📜 + check verde ✅ overlay en esquina inferior derecha.
// Tamaños configurables porque se usa en celda (sm), dropdown (md) y modal (lg).
const renderCotizacionEnviada = (size = 'md') => {
  const emojiSize = size === 'sm' ? '1.25rem' : size === 'lg' ? '1.875rem' : '1.5rem'
  const badgeSize = size === 'sm' ? '0.65rem' : size === 'lg' ? '0.95rem' : '0.8rem'
  const offset = size === 'sm' ? '-2px' : '-4px'
  return (
    <span className="relative inline-flex items-center justify-center" style={{ fontSize: emojiSize, lineHeight: 1, width: emojiSize, height: emojiSize }}>
      <span>{'📜'}</span>
      <span style={{ position: 'absolute', bottom: offset, right: offset, fontSize: badgeSize, lineHeight: 1, filter: 'drop-shadow(0 0 1px rgba(255,255,255,0.9))' }}>{'✅'}</span>
    </span>
  )
}

const SORTABLE_COLUMNS = {
  created_at:          { label: 'Creado',      type: 'date' },
  updated_at:          { label: 'Últ. Mod.',   type: 'date' },
  next_followup_date:  { label: 'Vencimiento', type: 'date' },
  name:                { label: 'Nombre',      type: 'string', width: 150 },
  phone:               { label: 'Teléfono',    type: 'string' },
  assigned_to:         { label: 'Asignado',    type: 'string' },
  hora:                { label: 'Hora',        type: 'date' },
  labels:              { label: 'Etiquetas',   type: 'array_count' },
  completed_campaigns: { label: 'Campañas',    type: 'array_count' },
  last_from_client:    { label: 'Esperando',   type: 'boolean' },
  bandera:             { label: 'Flag',        type: 'string' },
  icon_current:        { label: 'Icono',       type: 'string' },
  no_contesta_dia:     { label: 'DIA',         type: 'number' },
  hint:                { label: 'Nota Express', type: 'string' }
}

// Persistencia de sort per-agent
const SORT_STORAGE_PREFIX = 'crm_todolist_sort_'

const loadSortPref = (phone) => {
  try {
    const saved = localStorage.getItem(SORT_STORAGE_PREFIX + phone)
    if (saved) {
      const { column, direction } = JSON.parse(saved)
      if (SORTABLE_COLUMNS[column] && ['asc', 'desc'].includes(direction)) {
        return { column, direction }
      }
    }
  } catch {}
  return null
}

const saveSortPref = (phone, column, direction) => {
  try {
    localStorage.setItem(SORT_STORAGE_PREFIX + phone, JSON.stringify({ column, direction }))
  } catch {}
}

export default function ContactsTable({
  contacts,
  loading,
  selectedContact,
  onSelectContact,
  currentFolderId,
  folders = [],
  onAddToFolder,
  onRemoveFromFolder,
  // Props para selección múltiple
  selectedIds = [],
  onSelectionChange,
  // Props para infinite scroll
  hasMore = false,
  loadingMore = false,
  onLoadMore,
  onSortChange,
  sortConfig = [{ column: 'updated_at', direction: 'desc' }],
  sortLoadingColumn = null,
  // Props para columnas
  readOnly = false,
  visibleColumns,
  columnWidths = {},
  onColumnWidthChange,
  total = 0,
  onIconChanged,
  onCreateFolder,
  onNavigateToChat,
  draftPhones,
  onFlagToggle,
  onDiaReset,
  onDiaClick,
  onInlineMutation,
  onBulkIcon,
  onAssignAgent,
  onEmptyClick,
  iconFilter,
  onIconFilterChange,
  flagFilter,
  onFlagFilterChange,
  scopeFilters,
  // Escribieron hoy: Set de phones NUEVOS desde la ultima visita (chip + agrupados arriba).
  newWrotePhones,
  // External ref to the scroll container (forwarded from parent for scroll-anchoring).
  // Si se provee, se asigna en sincronía con el ref interno via callback ref.
  scrollRef,
}) {
  const [showFolderMenu, setShowFolderMenu] = useState(null) // contactId o null
  const [iconModalContact, setIconModalContact] = useState(null) // { phone, iconId } or null
  const [flagSelector, setFlagSelector] = useState(null) // { phone, bandera, x, y }
  const [assignSelector, setAssignSelector] = useState(null) // { phone, assigned_to, x, y }
  const [columnFilterPopup, setColumnFilterPopup] = useState(null) // { type: 'icon'|'flag', x, y }
  const [contextDescPopup, setContextDescPopup] = useState(null) // { x, y, iconId } | null  para right-click descripcion
  const flagMenuRef = useRef(null)
  const flagPos = useFlipDropdown({
    anchorX: flagSelector?.x ?? 0,
    anchorY: flagSelector?.y ?? 0,
    menuRef: flagMenuRef,
    isOpen: !!flagSelector,
  })
  const assignMenuRef = useRef(null)
  const assignPos = useFlipDropdown({
    anchorX: assignSelector?.x ?? 0,
    anchorY: assignSelector?.y ?? 0,
    menuRef: assignMenuRef,
    isOpen: !!assignSelector,
  })
  const [teamList, setTeamList] = useState([])
  // Sort: controlado por padre (TodoListView) via sortConfig prop + onSortChange
  const sortColumn = sortConfig[0]?.column || null
  const sortDirection = sortConfig[0]?.direction || 'desc'

  // Frozen mode selectors (grilla TodoList): dot azul cross-agent, zombies (barre), new rows (fade verde)
  const frozenDots = useCRMStore(s => s.frozen.crossAgentDots)
  const frozenZombies = useCRMStore(s => s.frozen.zombies)
  const frozenNewRows = useCRMStore(s => s.frozen.newRowsHighlight)
  const clearDot = useCRMStore(s => s.clearDot)
  const clearZombie = useCRMStore(s => s.clearZombie)

  // Refs para infinite scroll
  const scrollContainerRef = useRef(null)
  const sentinelRef = useRef(null)

  // Callback ref que sincroniza el ref interno y el `scrollRef` externo (scroll-anchoring).
  // useCallback estable para evitar mount/unmount loops.
  const setScrollRef = useCallback((node) => {
    scrollContainerRef.current = node
    if (typeof scrollRef === 'function') {
      scrollRef(node)
    } else if (scrollRef && typeof scrollRef === 'object') {
      scrollRef.current = node
    }
  }, [scrollRef])

  // Ref para Shift+Click selection (último índice clickeado)
  const lastClickedIndexRef = useRef(null)

  // Refs para long-press (3s) - navigate to chat
  const longPressTimerRef = useRef(null)
  const longPressFeedbackTimerRef = useRef(null)

  // Column visibility helper
  const isVis = (colId) => !visibleColumns || visibleColumns.includes(colId)

  // Column resize logic (performance-optimized: DOM during drag, state on mouseup)
  const liveWidthsRef = useRef(columnWidths)
  useEffect(() => { liveWidthsRef.current = columnWidths }, [columnWidths])

  const handleResizeStart = (colId, startX) => {
    const startWidth = liveWidthsRef.current[colId] || 80

    const onMouseMove = (e) => {
      const diff = e.clientX - startX
      const newWidth = Math.max(30, startWidth + diff)
      liveWidthsRef.current = { ...liveWidthsRef.current, [colId]: newWidth }
      document.querySelectorAll(`[data-col="${colId}"]`).forEach(cell => {
        cell.style.width = `${newWidth}px`
        cell.style.minWidth = `${newWidth}px`
      })
    }

    const onMouseUp = () => {
      document.removeEventListener('mousemove', onMouseMove)
      document.removeEventListener('mouseup', onMouseUp)
      if (onColumnWidthChange) onColumnWidthChange({ ...liveWidthsRef.current })
    }

    document.addEventListener('mousemove', onMouseMove)
    document.addEventListener('mouseup', onMouseUp)
  }

  // IntersectionObserver para infinite scroll
  useEffect(() => {
    if (!sentinelRef.current || !onLoadMore) return

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loadingMore && !loading) {
          onLoadMore()
        }
      },
      {
        root: scrollContainerRef.current,
        rootMargin: '100px',
        threshold: 0
      }
    )

    observer.observe(sentinelRef.current)
    return () => observer.disconnect()
  }, [hasMore, loadingMore, loading, onLoadMore])

  // (Persistencia de sort se maneja en TodoListView)

  // Comparador por columna (usado en multi-sort)
  const compareByColumn = (a, b, column, direction) => {
    const config = SORTABLE_COLUMNS[column]
    if (!config) return 0
    // hora: extraer time-of-day de next_followup_date, 00:00 como NULL
    if (column === 'hora') {
      const extractTimeOfDay = (d) => {
        if (!d || !d.includes('T')) return null
        const t = d.split('T')[1]?.substring(0, 5)
        if (!t || t === '00:00') return null
        const [hh, mm] = t.split(':').map(Number)
        return hh * 60 + mm
      }
      const tA = extractTimeOfDay(a.next_followup_date)
      const tB = extractTimeOfDay(b.next_followup_date)
      if (tA == null && tB == null) return 0
      if (tA == null) return 1
      if (tB == null) return -1
      return direction === 'asc' ? tA - tB : tB - tA
    }
    let valA = a[column]
    let valB = b[column]
    // last_from_client: cuadruple bucket bandera(0) -> urgente(1) -> mano(2) -> sin mano(3)
    if (column === 'last_from_client') {
      const bucketOf = (v, c) => (v && c.has_fondo_keyword) ? 0 : (v && c.has_urgent_keyword) ? 1 : (v ? 2 : 3)
      const bucketA = bucketOf(valA, a)
      const bucketB = bucketOf(valB, b)
      if (bucketA !== bucketB) return direction === 'desc' ? bucketA - bucketB : bucketB - bucketA
      return 0
    }
    // icon_current: sort por priority rank (numérico) en vez de string alfabético
    if (column === 'icon_current') {
      const prA = a.icon_priority != null ? a.icon_priority : 99
      const prB = b.icon_priority != null ? b.icon_priority : 99
      if (prA !== prB) return direction === 'asc' ? prA - prB : prB - prA
      return 0
    }
    if (config.type === 'icon') {
      const hasA = valA ? 1 : 0
      const hasB = valB ? 1 : 0
      if (hasA !== hasB) return hasB - hasA
      if (hasA === 0) return 0
      const strA = valA.toString().toLowerCase()
      const strB = valB.toString().toLowerCase()
      if (strA < strB) return direction === 'asc' ? -1 : 1
      if (strA > strB) return direction === 'asc' ? 1 : -1
      return 0
    }
    if (config.type === 'boolean') {
      const bA = valA ? 1 : 0
      const bB = valB ? 1 : 0
      if (bA !== bB) return direction === 'asc' ? bA - bB : bB - bA
      return 0
    }
    // no_contesta_dia (DÍA): sin día (null) = día MENOR → arriba en ASC, abajo en DESC.
    // Debe coincidir con el backend (pcd.no_contesta_dia ASC NULLS FIRST / DESC NULLS LAST).
    if (column === 'no_contesta_dia') {
      const dA = valA == null ? -1 : (Number(valA) || 0)
      const dB = valB == null ? -1 : (Number(valB) || 0)
      if (dA !== dB) return direction === 'asc' ? dA - dB : dB - dA
      return 0
    }
    if (valA == null && valB == null) return 0
    if (valA == null) return 1
    if (valB == null) return -1
    if (config.type === 'date') {
      valA = new Date(valA).getTime()
      valB = new Date(valB).getTime()
    } else if (config.type === 'number') {
      valA = Number(valA) || 0
      valB = Number(valB) || 0
    } else if (config.type === 'array_count') {
      valA = Array.isArray(valA) ? valA.length : 0
      valB = Array.isArray(valB) ? valB.length : 0
    } else if (config.type === 'string') {
      valA = (valA || '').toString().toLowerCase()
      valB = (valB || '').toString().toLowerCase()
    }
    if (valA < valB) return direction === 'asc' ? -1 : 1
    if (valA > valB) return direction === 'asc' ? 1 : -1
    return 0
  }

  // Ordenar contactos (client-side multi-columna, consistente con server-side).
  // En frozen mode, NO re-ordenar: el orden ya está fijado por applyServerUpdate
  // según el snapshot. Re-sortear aquí deshace la garantía de estabilidad cuando
  // un campo de sort cambia (ej: editar IC con sort por IC mueve la fila).
  const frozenActive = useCRMStore(s => s.frozen.active)
  const sortedContacts = useMemo(() => {
    let base
    if (frozenActive || !sortConfig || sortConfig.length === 0) base = contacts
    else base = [...contacts].sort((a, b) => {
      for (const s of sortConfig) {
        const cmp = compareByColumn(a, b, s.column, s.direction)
        if (cmp !== 0) return cmp
      }
      return 0
    })
    // "Escribieron hoy": los NUEVOS desde la ultima visita van agrupados arriba (gana al orden base).
    if (newWrotePhones && newWrotePhones.size > 0) {
      base = [...base].sort((a, b) => (newWrotePhones.has(b.phone) ? 1 : 0) - (newWrotePhones.has(a.phone) ? 1 : 0))
    }
    return base
  }, [contacts, sortConfig, frozenActive, newWrotePhones])

  // Click en header: delega ciclo de 3 estados al parent via onSortChange
  const handleSort = (column) => {
    if (!SORTABLE_COLUMNS[column]) return
    if (onSortChange) onSortChange(column)
  }

  /**
   * Manejar selección con soporte para Shift+Click y Ctrl+Click
   * - Shift+Click: Selecciona rango desde último click
   * - Ctrl/Cmd+Click: Toggle selección sin afectar otros
   * - Click normal en checkbox: Toggle selección
   * - Click normal en fila: Selecciona contacto para ver detalles
   */
  const handleSelectionClick = (e, contactId, currentIndex, isCheckbox = false) => {
    if (isCheckbox) e.stopPropagation()
    if (!onSelectionChange) return

    const isShift = e.shiftKey
    const isCtrl = e.ctrlKey || e.metaKey // metaKey para Mac
    const isCurrentlySelected = selectedIds.includes(contactId)

    // Solo procesar si es click en checkbox O si hay modifier keys
    if (!isCheckbox && !isShift && !isCtrl) {
      return // Click normal en fila: dejar que maneje onSelectContact
    }

    // Prevenir selección de contacto para ver detalles si hay modifiers
    if (isShift || isCtrl) {
      e.stopPropagation()
    }

    if (isShift && lastClickedIndexRef.current !== null) {
      // Shift+Click: Seleccionar rango
      const start = Math.min(lastClickedIndexRef.current, currentIndex)
      const end = Math.max(lastClickedIndexRef.current, currentIndex)

      // Obtener IDs de contactos en el rango (usando sortedContacts)
      const rangeIds = sortedContacts.slice(start, end + 1).map(c => c.id)

      // Agregar todos los del rango a la selección (sin duplicar)
      const newSelection = [...new Set([...selectedIds, ...rangeIds])]
      onSelectionChange(newSelection)
    } else if (isCtrl) {
      // Ctrl+Click: Toggle individual
      if (isCurrentlySelected) {
        onSelectionChange(selectedIds.filter(id => id !== contactId))
      } else {
        onSelectionChange([...selectedIds, contactId])
      }
      lastClickedIndexRef.current = currentIndex
    } else if (isCheckbox) {
      // Click normal en checkbox: Toggle individual
      if (isCurrentlySelected) {
        onSelectionChange(selectedIds.filter(id => id !== contactId))
      } else {
        onSelectionChange([...selectedIds, contactId])
      }
      lastClickedIndexRef.current = currentIndex
    }
  }

  // Long-press handlers (3s -> navigate to chat)
  const handleRowMouseDown = useCallback((contact, e) => {
    if (e.target.closest('button, input, [data-no-longpress]')) return
    const row = e.currentTarget
    longPressFeedbackTimerRef.current = setTimeout(() => {
      row.style.borderLeft = '4px solid #3b82f6'
    }, 1500)
    longPressTimerRef.current = setTimeout(() => {
      row.style.borderLeft = ''
      if (onNavigateToChat) onNavigateToChat(contact.phone)
    }, 3000)
  }, [onNavigateToChat])

  const handleRowMouseUp = useCallback(() => {
    clearTimeout(longPressTimerRef.current)
    clearTimeout(longPressFeedbackTimerRef.current)
    document.querySelectorAll('tr[data-longpress]').forEach(el => {
      el.style.borderLeft = ''
    })
  }, [])

  // Componente para header ordenable
  const SortableHeader = ({ column, children, className = '', center = false, filterActive = false, onFilterClick = null, onFilterClear = null }) => {
    const isSortable = !!SORTABLE_COLUMNS[column]
    const sortIdx = sortConfig ? sortConfig.findIndex(s => s.column === column) : -1
    const isActive = sortIdx !== -1
    const sortLevel = sortIdx + 1
    const sortDir = isActive ? sortConfig[sortIdx].direction : null
    const isLoadingSort = sortLoadingColumn === column
    const colConfig = ALL_COLUMNS.find(c => c.id === column || (column === 'icon_current' && c.id === 'icon'))
    const colId = colConfig ? colConfig.id : column
    const isResizable = colConfig ? colConfig.resizable !== false : true

    const isFilterActive = filterActive;

    // Flecha SVG con stroke-width grueso (doble grosor vs Unicode)
    // Para icon_current invertimos visualmente: su "primer click" es ASC (rank 1 = más importante)
    // pero el usuario espera ver flecha hacia abajo en 1er click (convención de otras columnas)
    // Tamaño mayor para columnas con filtro (FLAG, IC) — son botones dedicados que deben ser visibles en cualquier DPI
    // CRMObs A (2026-05-15): si la columna esta cargando post-sort-change, mostrar
    // spinner para indicar que las urgentes/otras paginas estan en camino.
    const SortArrow = ({ big = false } = {}) => {
      const sz = big ? 20 : 14
      if (isLoadingSort) {
        return (
          <svg width={sz} height={sz} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" className="animate-spin">
            <path d="M21 12a9 9 0 11-6.219-8.56" />
          </svg>
        )
      }
      if (isActive) {
        const visualUp = column === 'icon_current' ? sortDir === 'desc' : sortDir === 'asc'
        return visualUp
          ? <svg width={sz} height={sz} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 15 12 9 18 15" /></svg>
          : <svg width={sz} height={sz} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9" /></svg>
      }
      return <svg width={big ? 18 : 13} height={big ? 18 : 13} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="7 15 12 20 17 15" /><polyline points="17 9 12 4 7 9" /></svg>
    }

    return (
      <th
        className={`px-2 py-1.5 ${center ? 'text-center' : 'text-left'} text-sm font-medium uppercase tracking-wider relative group border-r border-gray-400 dark:border-gray-600 last:border-r-0 ${
          isFilterActive ? 'bg-gradient-to-b from-blue-500 to-indigo-600 text-white shadow-inner ring-2 ring-blue-300 ring-inset' : isActive ? 'bg-purple-100 dark:bg-purple-900/40 text-purple-800 dark:text-purple-200 font-bold' : 'text-gray-700 dark:text-gray-300'} ${
          (isSortable || onFilterClick) ? 'cursor-pointer hover:bg-gray-100 dark:hover:bg-surface-dark-hover select-none' : ''} ${className}`}
        onClick={(e) => {
          // Shift+click siempre ordena (atajo B)
          if (e.shiftKey && isSortable) {
            e.preventDefault()
            handleSort(column)
            return
          }
          if (onFilterClick) {
            const r = e.currentTarget.getBoundingClientRect()
            onFilterClick(r.left, r.bottom + 4)
          } else if (isSortable) {
            handleSort(column)
          }
        }}
        style={colConfig && colConfig.width ? { width: columnWidths[colId] || colConfig.width, minWidth: columnWidths[colId] || colConfig.width, maxWidth: columnWidths[colId] || colConfig.width } : undefined}
        data-col={colId}
        title={onFilterClick && isSortable ? 'Click: filtrar \u00B7 Shift+Click o icono: ordenar' : undefined}
      >
        <div className={`flex items-center gap-1 ${center ? 'justify-center' : ''}`}>
          {children}
          {isSortable && (
            onFilterClick ? (
              // Columnas con filtro (FLAG, IC): icono dedicado clickeable para ordenar.
              // Tama\u00F1o grande para visibilidad en pantallas con escalado raro (Windows DPI).
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); handleSort(column) }}
                className={`flex items-center leading-none shrink-0 px-1.5 py-1 rounded-md border hover:bg-black/10 dark:hover:bg-white/10 transition ${
                  isFilterActive
                    ? 'text-white border-white/40 bg-white/15'
                    : isActive
                      ? 'text-purple-700 dark:text-purple-300 border-purple-300 dark:border-purple-700 bg-white/60 dark:bg-purple-900/20'
                      : 'text-gray-600 dark:text-gray-300 border-gray-300 dark:border-gray-600 bg-white/70 dark:bg-slate-800/60'
                }`}
                title="Ordenar por esta columna"
              >
                {isActive && sortConfig && sortConfig.length > 1 && (
                  <span className="text-sm font-bold mr-0.5">{sortLevel === 1 ? '\u00B9' : sortLevel === 2 ? '\u00B2' : ''}</span>
                )}
                <SortArrow big />
              </button>
            ) : (
              // Columnas normales: flecha indicadora (click del th ordena)
              <span className={`flex items-center leading-none shrink-0 ${isActive ? 'text-purple-700 dark:text-purple-300' : 'text-gray-500 dark:text-gray-400'}`}>
                {isActive && sortConfig && sortConfig.length > 1 && (
                  <span className="text-xs font-bold mr-0.5">{sortLevel === 1 ? '\u00B9' : sortLevel === 2 ? '\u00B2' : ''}</span>
                )}
                <SortArrow />
              </span>
            )
          )}
          {onFilterClick && (
            <span className={`ml-0.5 ${isFilterActive ? 'text-white' : 'text-gray-400'}`} title="Filtrar">
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                <polygon points="3 4 21 4 14 14 14 20 10 22 10 14 3 4" />
              </svg>
            </span>
          )}
          {isFilterActive && onFilterClear && (
            <button
              onClick={(e) => { e.stopPropagation(); onFilterClear() }}
              className="ml-1 w-4 h-4 flex items-center justify-center rounded-full bg-white/20 hover:bg-white/40 text-white transition"
              title="Quitar filtro"
            >
              <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="4" strokeLinecap="round">
                <line x1="6" y1="6" x2="18" y2="18" />
                <line x1="18" y1="6" x2="6" y2="18" />
              </svg>
            </button>
          )}
        </div>
        {isResizable && (
          <div
            className="absolute right-0 top-0 bottom-0 w-1.5 cursor-col-resize opacity-0 group-hover:opacity-100 hover:bg-primary-400/50 transition-opacity"
            onMouseDown={(e) => {
              e.stopPropagation()
              handleResizeStart(colId, e.clientX)
            }}
          />
        )}
      </th>
    )
  }

  const handleDragStart = (e, contact) => {
    e.dataTransfer.setData('application/json', JSON.stringify({
      contactId: contact.id,
      sourceFolderId: currentFolderId
    }))
    e.dataTransfer.effectAllowed = 'move'
  }

  // Popup hover con ultimos 3 mensajes del cliente
  const [hoverPopup, setHoverPopup] = useState(null)
  const hoverTimerRef = useRef(null)
  const msgCacheRef = useRef(new Map())

  const handleHandMouseEnter = (e, phone) => {
    if (!phone) return
    clearTimeout(hoverTimerRef.current)
    const rect = e.currentTarget.getBoundingClientRect()
    const pos = { x: rect.left + rect.width / 2, y: rect.bottom + 6 }
    hoverTimerRef.current = setTimeout(async () => {
      const cached = msgCacheRef.current.get(phone)
      if (cached) {
        setHoverPopup({ phone, ...pos, messages: cached, loading: false })
        return
      }
      setHoverPopup({ phone, ...pos, messages: [], loading: true })
      try {
        const mod = await import('../../services/api')
        const msgs = await mod.messagesAPI.getLastClientMessages(phone, 3)
        msgCacheRef.current.set(phone, msgs)
        setHoverPopup(prev => (prev && prev.phone === phone) ? { ...prev, messages: msgs, loading: false } : prev)
      } catch (err) {
        console.warn('last-client error', err)
        setHoverPopup(prev => (prev && prev.phone === phone) ? { ...prev, loading: false, error: true } : prev)
      }
    }, 400)
  }

  const handleHandMouseLeave = () => {
    clearTimeout(hoverTimerRef.current)
    setHoverPopup(null)
  }

  const formatPopupTime = (ts) => {
    if (!ts) return ''
    const d = new Date(ts)
    if (isNaN(d.getTime())) return ''
    const dd = String(d.getDate()).padStart(2, '0')
    const mm = String(d.getMonth() + 1).padStart(2, '0')
    const hh = String(d.getHours()).padStart(2, '0')
    const mi = String(d.getMinutes()).padStart(2, '0')
    return `${dd}/${mm} ${hh}:${mi}`
  }

  const formatDateOnly = (dateString) => {
    if (!dateString) return '-'
    try {
      const d = new Date(dateString)
      if (isNaN(d.getTime())) return '-'
      const day = String(d.getDate()).padStart(2, '0')
      const month = String(d.getMonth() + 1).padStart(2, '0')
      return day + '/' + month
    } catch {
      return '-'
    }
  }

  const formatDate = (dateString) => {
    if (!dateString) return '-'
    try {
      const d = new Date(dateString)
      if (isNaN(d.getTime())) return '-'
      const day = String(d.getDate()).padStart(2, '0')
      const month = String(d.getMonth() + 1).padStart(2, '0')
      const hh = String(d.getHours()).padStart(2, '0')
      const mm = String(d.getMinutes()).padStart(2, '0')
      return day + '/' + month + ' ' + hh + ':' + mm
    } catch {
      return '-'
    }
  }

  const formatDateTime = (dateString) => {
    if (!dateString) return '-'
    try {
      const d = new Date(dateString)
      if (isNaN(d.getTime())) return '-'
      const day = String(d.getDate()).padStart(2, '0')
      const month = String(d.getMonth() + 1).padStart(2, '0')
      const hh = String(d.getHours()).padStart(2, '0')
      const mm = String(d.getMinutes()).padStart(2, '0')
      return day + '/' + month + ' ' + hh + ':' + mm
    } catch {
      return '-'
    }
  }

  const isOverdue = (dateString) => {
    if (!dateString) return false
    try {
      // Comparar solo la parte de fecha (YYYY-MM-DD) para evitar problemas de timezone
      const dateOnly = dateString.split('T')[0]
      const today = new Date()
      const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`
      return dateOnly < todayStr
    } catch {
      return false
    }
  }

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  const hasColumnFilter = (iconFilter !== null && iconFilter !== undefined) || (flagFilter !== null && flagFilter !== undefined)
  if (contacts.length === 0 && !hasColumnFilter) {
    return (
      <div className="flex-1 flex items-center justify-center text-gray-500">
        No hay contactos en esta lista
      </div>
    )
  }

  return (
    <div ref={setScrollRef} className="flex-1 overflow-auto bg-white dark:bg-surface-dark"
      onClick={(e) => {
        // Close panel when clicking empty space (not on a table row or popup)
        const tag = e.target.tagName.toLowerCase()
        const isRow = e.target.closest('tr') || e.target.closest('button') || e.target.closest('[class*="fixed"]') || tag === 'input'
        if (!isRow && onEmptyClick) onEmptyClick()
      }}
    >
      <style>{`
        @keyframes marquee-scroll {
          0%, 10% { transform: translateX(0); }
          90%, 100% { transform: translateX(var(--scroll-dist, -50%)); }
        }

        /* Frozen mode visual indicators */

        /* Dot azul cross-agent (reasignacion): pseudo-element absolute en la
           primera celda, centrado vertical y fijo al borde izquierdo. Asi se
           mantiene alineado en todas las filas sin depender del contenido. */
        tr[data-frozen-state="dot"] td:first-child {
          position: relative;
        }
        tr[data-frozen-state="dot"] td:first-child::before {
          content: '';
          position: absolute;
          left: 2px;
          top: 50%;
          transform: translateY(-50%);
          width: 7px;
          height: 7px;
          background: #3b82f6;
          border-radius: 50%;
          box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.15);
          pointer-events: none;
        }

        /* Zombie: opacity reducida + strikethrough */
        tr[data-frozen-state="zombie"] {
          opacity: 0.5;
          transition: opacity 0.4s ease, transform 0.4s ease;
        }
        tr[data-frozen-state="zombie"] td {
          text-decoration: line-through;
          color: #6b7280;
        }
        tr[data-frozen-state="zombie"]:hover {
          opacity: 0.7;
        }

        /* New row: fade verde 5s */
        @keyframes frozenNewFade {
          0%   { background-color: #a7f3d0; }
          80%  { background-color: #ecfdf5; }
          100% { background-color: transparent; }
        }
        tr[data-frozen-state="new"] {
          animation: frozenNewFade 5s ease-out forwards;
        }
        .dark tr[data-frozen-state="new"] {
          animation-name: frozenNewFadeDark;
        }
        @keyframes frozenNewFadeDark {
          0%   { background-color: rgba(16, 185, 129, 0.3); }
          100% { background-color: transparent; }
        }

        /* Suave transición al reordenar (útil al descongelar). Incluye background-color
           para preservar el hover fade original (Tailwind transition-all duration-150). */
        tr[data-row-flip] {
          transition: transform 250ms ease-out, opacity 200ms ease, background-color 150ms ease;
        }
      `}</style>
      <table className="min-w-full divide-y divide-gray-100 dark:divide-surface-dark-border" style={{ tableLayout: 'fixed' }}>
        <thead className="bg-gradient-to-r from-gray-50 to-gray-100 dark:from-surface-dark-alt dark:to-surface-dark-alt sticky top-0 shadow-sm z-10 border-b-2 border-gray-400 dark:border-gray-600">
          <tr>
            {/* Checkbox selección múltiple */}
            {onSelectionChange && (
              <th className="px-1 py-1.5 w-7 border-r border-gray-400 dark:border-gray-600">
                <input
                  type="checkbox"
                  checked={contacts.length > 0 && selectedIds.length === contacts.length}
                  onChange={(e) => {
                    if (e.target.checked) {
                      onSelectionChange(contacts.map(c => c.id))
                    } else {
                      onSelectionChange([])
                    }
                  }}
                  className="w-4 h-4 text-primary-600 border-gray-300 dark:border-gray-600 rounded focus:ring-primary-500 cursor-pointer"
                />
              </th>
            )}
            {isVis('created_at') && <SortableHeader column="created_at">Creado</SortableHeader>}
            {isVis('updated_at') && <SortableHeader column="updated_at">Últ. Mod.</SortableHeader>}
            {isVis('next_followup_date') && <SortableHeader column="next_followup_date">Venc.</SortableHeader>}
            {isVis('next_followup_date') && <SortableHeader column="hora" className="w-[50px]">Hora</SortableHeader>}
            <SortableHeader column="bandera" center filterActive={flagFilter !== null && flagFilter !== undefined} onFilterClick={(x,y) => setColumnFilterPopup({ type: 'flag', x, y })} onFilterClear={() => onFlagFilterChange && onFlagFilterChange(null)}>Flag</SortableHeader>
            {isVis('icon') && <SortableHeader column="icon_current" className="w-14" center filterActive={iconFilter !== null && iconFilter !== undefined} onFilterClick={(x,y) => setColumnFilterPopup({ type: 'icon', x, y })} onFilterClear={() => onIconFilterChange && onIconFilterChange(null)}>IC</SortableHeader>}
            <SortableHeader column="no_contesta_dia" className="w-[55px]" center>DÍA</SortableHeader>
            {isVis('name') && <SortableHeader column="name">Nombre</SortableHeader>}
            {isVis('waiting') && <SortableHeader column="last_from_client" center>✋</SortableHeader>}
                <SortableHeader column="hint" className="w-[200px]">Nota Express</SortableHeader>
            {isVis('phone') && <SortableHeader column="phone">Teléfono</SortableHeader>}
            {isVis('assigned_to') && <SortableHeader column="assigned_to">Asignado</SortableHeader>}
            {isVis('labels') && <SortableHeader column="labels" className="w-16" center>🏷️</SortableHeader>}
            {isVis('campaigns') && <SortableHeader column="completed_campaigns" className="w-12" center>📧</SortableHeader>}
            {isVis('folder_actions') && <th data-col="folder_actions" className="px-2 py-1.5 text-center text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider border-r border-gray-400 dark:border-gray-600 last:border-r-0" style={{ width: columnWidths.folder_actions || 40, minWidth: columnWidths.folder_actions || 40 }}>
              📋
            </th>}
          </tr>
        </thead>
        <tbody className="bg-white dark:bg-surface-dark divide-y divide-gray-100 dark:divide-surface-dark-border">
          {sortedContacts.length === 0 && (
            <tr>
              <td colSpan={20} className="text-center py-8 text-gray-500 dark:text-gray-400">
                No hay contactos que coincidan con el filtro. Haz click en la × junto al título de la columna (IC o Flag) para quitar el filtro.
              </td>
            </tr>
          )}
          {sortedContacts.map((contact, index) => {
            const isSelected = selectedContact?.id === contact.id
            const isChecked = selectedIds.includes(contact.id)
            const dateUrgency = getDateUrgency(contact.next_followup_date)
            const phone = contact.phone
            const isDot = !!frozenDots[phone]
            const isZombie = !!frozenZombies[phone]
            const isNew = !!frozenNewRows[phone]
            const isNewWrote = !!(newWrotePhones && newWrotePhones.has(phone))
            const frozenState = isNew ? 'new' : (isZombie ? 'zombie' : (isDot ? 'dot' : undefined))

            // CoSelect: anillos concentricos por agentes mirando
            const watching = contact.watching_agents || []
            const shownWatchers = watching.slice(0, 3)
            const overflowWatchers = watching.length - shownWatchers.length
            const ringShadow = shownWatchers.length === 0
              ? undefined
              : shownWatchers.map((agent, i) => {
                  const c = colorForAgent(agent)
                  const spread = 2 + (i * 4) // 2, 6, 10 px
                  return `0 0 0 ${spread}px ${c}`
                }).join(', ')
            const watchingTooltip = watching.length > 0
              ? (overflowWatchers > 0
                  ? `Mirando: ${shownWatchers.join(', ')} y ${overflowWatchers} más (${watching.slice(3).join(', ')})`
                  : `Mirando: ${watching.join(', ')}`)
              : undefined

            return (
              <tr
                key={contact.id}
                data-phone={phone}
                data-frozen-state={frozenState}
                data-row-flip="true"
                data-watching-count={watching.length}
                title={watchingTooltip}
                className={`
                  cursor-pointer transition-all duration-150
                  ${isChecked
                    ? 'bg-primary-50 dark:bg-primary-900/30 border-l-4 border-l-primary-500'
                    : (draftPhones && draftPhones.has && draftPhones.has(contact.phone))
                      ? 'bg-yellow-200 dark:bg-yellow-900/50 animate-pulse-subtle'
                      : isSelected
                        ? 'bg-red-50 dark:bg-red-900/30'
                        : index % 2 === 0
                          ? 'bg-white dark:bg-surface-dark hover:bg-gray-50 dark:hover:bg-surface-dark-hover/50'
                          : 'bg-gray-200 dark:bg-slate-800/50 hover:bg-gray-300/60 dark:hover:bg-surface-dark-hover/50'
                  }
                  ${isSelected ? 'outline outline-2 outline-red-500 -outline-offset-2' : ''}
                `}
                style={undefined}
                onClick={(e) => {
                  // Frozen-mode pre-checks: zombies barre+stop, dot limpia silenciosamente
                  if (isZombie) {
                    clearZombie(phone)
                    e.stopPropagation()
                    return
                  }
                  if (isDot) clearDot(phone)
                  // Si hay Shift o Ctrl, manejar selección múltiple
                  if ((e.shiftKey || e.ctrlKey || e.metaKey) && onSelectionChange) {
                    handleSelectionClick(e, contact.id, index, false)
                  } else {
                    // Click normal: seleccionar contacto para ver detalles
                    onSelectContact(contact)
                  }
                }}
                draggable
                onDragStart={(e) => handleDragStart(e, contact)}
                data-longpress
                onMouseDown={(e) => handleRowMouseDown(contact, e)}
                onMouseUp={handleRowMouseUp}
                onMouseLeave={handleRowMouseUp}
              >
                {/* Checkbox selección múltiple con soporte Shift+Click y Ctrl+Click */}
                {onSelectionChange && isVis('checkbox') && (
                  <td data-col="checkbox" className="py-1 pl-2 pr-1 border-r border-gray-400 dark:border-gray-600 relative" style={{ width: 32, minWidth: 32 }}>
                    {shownWatchers.length > 0 && (
                      <div className="absolute left-0 top-0 bottom-0 flex pointer-events-none">
                        {shownWatchers.map((agent) => (
                          <div key={agent} style={{ width: '2px', backgroundColor: colorForAgent(agent) }} />
                        ))}
                      </div>
                    )}
                    <div className="flex items-center gap-1">
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onClick={(e) => handleSelectionClick(e, contact.id, index, true)}
                        onChange={() => {}} // Manejado en onClick para capturar modifiers
                        className="w-4 h-4 text-primary-600 border-gray-300 dark:border-gray-600 rounded focus:ring-primary-500 cursor-pointer"
                      />
                    </div>
                  </td>
                )}
                {/* Fecha creación */}
                {isVis('created_at') && <td data-col="created_at" className="px-0.5 py-0 whitespace-nowrap leading-none text-gray-800 dark:text-gray-300 truncate text-center border-r border-gray-400 dark:border-gray-600" style={{ width: columnWidths.created_at || 95, minWidth: columnWidths.created_at || 95 }}>
                  <span className="text-xl">{formatDateOnly(contact.created_at)}</span>
                  {contact.created_at && (() => {
                    const d = new Date(contact.created_at);
                    if (isNaN(d.getTime())) return null;
                    const hh = String(d.getHours()).padStart(2,'0');
                    const mm = String(d.getMinutes()).padStart(2,'0');
                    return <span className="text-[0.87rem] text-gray-500 dark:text-gray-400 ml-0.5 align-middle">{hh + ':' + mm}</span>;
                  })()}
                </td>}

                {/* Última modificación */}
                {isVis('updated_at') && <td data-col="updated_at" className="px-0.5 py-0 whitespace-nowrap text-xl leading-none text-gray-800 dark:text-gray-300 truncate text-center border-r border-gray-400 dark:border-gray-600" style={{ width: columnWidths.updated_at || 125, minWidth: columnWidths.updated_at || 125 }}>
                  {formatDateTime(contact.updated_at)}
                </td>}

                {/* Vencimiento - simplified date only */}
                {isVis('next_followup_date') && <td data-col="next_followup_date" className={`px-0.5 py-0 whitespace-nowrap text-xl leading-none border-r border-gray-400 dark:border-gray-600 ${dateUrgency.cellClass}`} style={{ width: columnWidths.next_followup_date || 80, minWidth: columnWidths.next_followup_date || 80 }}>
                  {/* Edición de fecha se hace desde el panel del prospecto, no desde la grilla.
                      Click en la celda hace bubble al onClick del <tr> (selecciona contacto). */}
                  <span className="text-gray-800 dark:text-gray-300">
                    {contact.next_followup_date
                      ? (() => { const [y,m,d] = contact.next_followup_date.split('T')[0].split('-'); return d + '/' + m })()
                      : <span className="text-gray-400">-</span>
                    }
                  </span>
                </td>}
                {/* Hora column */}
                {isVis('next_followup_date') && <td className="relative px-0.5 py-0 whitespace-nowrap text-xl leading-none text-gray-800 dark:text-gray-300 text-center border-r border-gray-400 dark:border-gray-600" style={{ width: 50, minWidth: 50 }}>
                  {contact.next_followup_date && contact.next_followup_date.includes('T')
                    ? (() => { const t = contact.next_followup_date.split('T')[1]?.substring(0,5); return t && t !== '00:00' ? t : '-' })()
                    : '-'}
                  {/* Ticket "Trabajado hoy": cuadrado verde 8x8 con glow sutil, anclado
                      al borde derecho de la celda (absolute) para que se mantenga
                      alineado independiente de si la fila tiene hora o "-". */}
                  {contact.worked_today_by_me && (
                    <span
                      className="absolute right-1 top-1/2 -translate-y-1/2 inline-block w-2 h-2 rounded-sm bg-emerald-500"
                      style={{ boxShadow: '0 0 4px rgba(16, 185, 129, 0.6)' }}
                      title="Trabajado hoy"
                      aria-label="Trabajado hoy"
                    />
                  )}
                </td>}

                {/* Bandera/Flag - click to toggle (optimistic UI) */}
                <td data-col="bandera" className="px-0.5 py-0 text-center border-r border-gray-400 dark:border-gray-600 cursor-pointer" style={{ width: columnWidths.bandera || 70, minWidth: columnWidths.bandera || 70 }}
                    onClick={(e) => {
                      if (readOnly) return
                      e.stopPropagation()
                      if (onSelectContact) onSelectContact(contact)
                      const rect = e.currentTarget.getBoundingClientRect()
                      setFlagSelector(flagSelector && flagSelector.phone === contact.phone ? null : { phone: contact.phone, bandera: contact.bandera, x: rect.right + 4, y: rect.top })
                    }}
                    title={contact.bandera ? (FLAG_LABEL[contact.bandera] || 'Flag') : 'Marcar flag'}
                >
                  {contact.bandera && (contact.bandera === 'whatsapp' ? <svg className="w-5 h-5 text-green-500 inline" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/><path d="M12 2C6.486 2 2 6.486 2 12c0 1.86.507 3.6 1.389 5.09L2.07 21.78a.5.5 0 00.612.638l4.857-1.3A9.95 9.95 0 0012 22c5.514 0 10-4.486 10-10S17.514 2 12 2z"/></svg> : contact.bandera === 'wassenger' ? <svg className="w-5 h-5 text-blue-500 inline" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/><path d="M12 2C6.486 2 2 6.486 2 12c0 1.86.507 3.6 1.389 5.09L2.07 21.78a.5.5 0 00.612.638l4.857-1.3A9.95 9.95 0 0012 22c5.514 0 10-4.486 10-10S17.514 2 12 2z"/></svg> : contact.bandera === 'cotizacion_enviada' ? renderCotizacionEnviada('sm') : contact.bandera === 'reciclado_hoy' ? <span className="text-xl" style={{ filter: 'sepia(1) saturate(7) hue-rotate(16deg) brightness(1.08)' }}>{String.fromCodePoint(0x267B, 0xFE0F)}</span> : <span className="text-xl">{FLAG_EMOJI[contact.bandera] || FLAG_EMOJI.bandera}</span>)}
                </td>
                {/* Icono con historial */}
                {isVis('icon') && (
                  <td data-col="icon" className="relative px-1 py-0.5 text-center cursor-pointer hover:bg-gray-100 dark:hover:bg-surface-dark-hover transition border-r border-gray-400 dark:border-gray-600" style={{ width: columnWidths.icon || 40, minWidth: columnWidths.icon || 40 }}
                      onClick={(e) => {
                        if (readOnly) return
                        e.stopPropagation()
                        if (selectedIds.length >= 2) {
                          // 2+ selected: open in bulk mode (phone=null triggers bulk in modal)
                          setIconModalContact({ phone: null, iconId: null, name: null })
                        } else {
                          if (onSelectContact) onSelectContact(contact)
                          setIconModalContact({ phone: contact.phone, iconId: contact.icon_current, name: contact.name })
                        }
                      }}
                      onContextMenu={(e) => {
                        e.preventDefault()
                        const raw = contact.icon_current || 'bulb_off'
                        const iconKey = NOMBRE_MAP[raw] || raw
                        setContextDescPopup({ x: e.clientX, y: e.clientY, iconId: iconKey })
                      }}
                  >
                    <IconHistoryTooltip iconsData={contact.icons_data} currentIcon={contact.icon_current} />
    <ScarBadge level={contact.scar_level} />
                  </td>
                )}
                <td
                  className="px-0.5 py-0 text-center text-xl leading-none text-gray-800 dark:text-gray-300 border-r border-gray-400 dark:border-gray-600 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800"
                  style={{ width: 55, minWidth: 55 }}
                  onClick={(e) => { e.stopPropagation(); if (!readOnly && onDiaClick) onDiaClick(contact); }}
                  title="Click para cambiar dia del ciclo"
                >
                  {contact.no_contesta_dia != null ? (
                    <span className="inline-flex items-center gap-1 whitespace-nowrap">
                      <span>(DIA {contact.no_contesta_dia})</span>
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          if (readOnly) return
                          if (onDiaReset) onDiaReset(contact.phone)
                          import('../../services/api').then(({ prospectAPI }) => {
                            prospectAPI.updateProspect(contact.phone, { no_contesta_dia: null }).catch(() => {})
                          })
                        }}
                        className="flex-shrink-0 text-gray-400 hover:text-red-500 dark:hover:text-red-400 cursor-pointer transition-colors duration-200"
                        title="Resetear DIA"
                      ><svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg></button>
                    </span>
                  ) : (
                    <span className="text-gray-400 dark:text-gray-600 text-sm">+</span>
                  )}
                </td>

                {/* Nombre con Avatar y Badge Vendido */}
                {isVis('name') && <td data-col="name" className={`px-0.5 py-0 whitespace-nowrap overflow-hidden border-r border-gray-400 dark:border-gray-600${(contact.completed_campaigns?.length > 0) ? ' bg-gradient-to-r from-pink-100/40 to-fuchsia-100/40 dark:from-pink-900/15 dark:to-fuchsia-900/15' : ''}`} style={{ width: columnWidths.name || 150, minWidth: columnWidths.name || 150, maxWidth: columnWidths.name || 150 }}>
                  {/* Procedencia del contacto, en el mismo lugar y forma: NARANJA = se registró en
                      la tienda online, VERDE = lo creó un agente a mano. El naranja tiene
                      PRECEDENCIA porque un contacto nacido del registro de la tienda cumple también
                      la condición de is_manual (wid = telefono y sin historial), y sin este orden
                      se vería verde y perderíamos justo el dato nuevo. */}
                  <div className={`flex items-center gap-2 ${contact.is_tienda ? 'bg-gradient-to-r from-orange-100 to-amber-50 dark:from-orange-900/30 dark:to-amber-900/20 -mx-2 px-2 py-px rounded-lg border border-orange-200/60 dark:border-orange-700/40' : contact.is_manual ? 'bg-gradient-to-r from-emerald-100 to-green-50 dark:from-emerald-900/30 dark:to-green-900/20 -mx-2 px-2 py-px rounded-lg border border-emerald-200/60 dark:border-emerald-700/40' : ''}`}>
                    <ContactAvatar
                      name={contact.name} phone={contact.phone} seed={contact.avatar_seed}
                      gradientOverride={contact.is_tienda ? 'linear-gradient(135deg, #F97316, #FCD34D)' : contact.is_manual ? 'linear-gradient(135deg, #10B981, #6EE7B7)' : null}
                      title={contact.is_tienda ? 'Se registró en la tienda online' : contact.is_manual ? 'Contacto creado manualmente' : ''}
                      className="w-7 h-7 rounded-full flex-shrink-0 text-sm shadow-sm" />
                    <span
                      className="text-xl leading-none text-gray-900 dark:text-gray-100 overflow-hidden block truncate"
                      onMouseEnter={(e) => {
                        const el = e.currentTarget;
                        if (el.scrollWidth > el.clientWidth) {
                          const dist = el.clientWidth - el.scrollWidth;
                          el.style.setProperty('--scroll-dist', dist + 'px');
                          el.style.overflow = 'visible';
                          el.firstChild.style.display = 'inline-block';
                          el.firstChild.style.animation = 'marquee-scroll 3s linear infinite';
                        }
                      }}
                      onMouseLeave={(e) => {
                        const el = e.currentTarget;
                        el.style.overflow = 'hidden';
                        if (el.firstChild) {
                          el.firstChild.style.animation = 'none';
                          el.firstChild.style.display = '';
                        }
                      }}
                    >
                      <span>{contact.name || contact.phone}</span>
                    </span>
                    {/* Grupo derecho: chip (izq) + boton copiar (der), alineado entre filas. 2026-06-23 */}
                    <span className="ml-auto flex items-center gap-1.5 flex-shrink-0">
                    {isNewWrote && (
                      <span
                        className="inline-flex items-center gap-1 px-1.5 py-px rounded-full text-[10px] font-semibold bg-emerald-500 text-white whitespace-nowrap shadow-sm"
                        title="Nuevo desde tu última visita a Escribieron hoy"
                      >
                        ✨ nuevo
                      </span>
                    )}
                    {contact.wrote_today_by_client && (
                      contact.wrote_today_processed ? (
                        <span
                          className="inline-block align-middle text-2xl leading-none"
                          title="Escribió hoy y ya lo procesaste con nota"
                        >
                          😊
                        </span>
                      ) : (
                        <span
                          className="inline-flex items-center gap-1 px-1.5 py-px rounded-full text-[10px] font-medium bg-fuchsia-100 text-fuchsia-700 dark:bg-fuchsia-900/30 dark:text-fuchsia-300 whitespace-nowrap"
                          title="Escribió hoy por WhatsApp"
                        >
                          ✍️ hoy
                        </span>
                      )
                    )}
                    <button
                      onClick={(e) => { e.stopPropagation(); const btn = e.currentTarget; navigator.clipboard.writeText(contact.phone).then(() => { btn.classList.add('text-green-500'); btn.querySelector('.icon-copy').style.display = 'none'; btn.querySelector('.icon-check').style.display = 'inline'; setTimeout(() => { try { btn.classList.remove('text-green-500'); btn.querySelector('.icon-copy').style.display = 'inline'; btn.querySelector('.icon-check').style.display = 'none' } catch(ex){} }, 1500) }) }}
                      className="flex-shrink-0 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 cursor-pointer transition-colors duration-200"
                      title="Copiar telefono"
                    ><svg xmlns="http://www.w3.org/2000/svg" className="icon-copy h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg><svg xmlns="http://www.w3.org/2000/svg" className="icon-check h-4 w-4" style={{display:'none'}} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg></button>
                    </span>
                    {/* Badge Vendido */}
                    {contact.vendido && (
                      <span
                        className="inline-flex items-center gap-1 px-2 py-px rounded-full text-xs font-medium bg-gradient-to-r from-green-100 to-emerald-100 text-green-800 border border-green-200"
                        title={contact.venta_monto ? `Venta: $${contact.venta_monto.toLocaleString('es-CL')}` : 'Vendido'}
                      >
                        <span className="text-green-600">✓</span>
                        Vendido
                      </span>
                    )}
                  </div>
                </td>}

                {/* Teléfono */}

                {/* Waiting (ultimo mensaje del cliente) */}
                {isVis('waiting') && (
                  <td data-col="waiting" className="px-0.5 py-0 text-center border-r border-gray-400 dark:border-gray-600 cursor-help" style={{ width: 45, minWidth: 45 }} onMouseEnter={(e) => handleHandMouseEnter(e, contact.phone)} onMouseLeave={handleHandMouseLeave}>
                    {contact.last_from_client && (contact.has_fondo_keyword
                      ? <span className="fondo-flag" title="FONDO ESTATAL: Menciona SERCOTEC/FOSIS/Ruta Digital/CRECE/proyecto/gané/concurso"><ChileFlagIcon /></span>
                      : contact.has_urgent_keyword
                        ? <span className="urgent-hand" title="URGENTE: Menciona llamar/hoy/ahora/etc."><span className="text-lg leading-none">✋</span></span>
                        : <span className="text-2xl leading-none" title="Esperando nuestra respuesta">✋</span>
                    )}
                  </td>
                )}

                {/* Hint */}
                <td className="px-0.5 py-0 whitespace-nowrap overflow-hidden border-r border-gray-400 dark:border-gray-600" style={{ width: 200, minWidth: 200 }}>
                  <input
                    type="text"
                    defaultValue={contact.hint || ''}
                    placeholder="..."
                    readOnly={readOnly}
                    onBlur={(e) => {
                      if (readOnly) return
                      const val = e.target.value.trim()
                      if (val !== (contact.hint || '')) {
                        // Pasar val al callback para que TodoListView haga optimistic
                        // del hint + last_from_client (baja la mano).
                        if (onInlineMutation) onInlineMutation(contact.phone, val)
                        import('../../services/api').then(({ prospectAPI }) => {
                          prospectAPI.updateProspect(contact.phone, { hint: val })
                        })
                      }
                    }}
                    onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur() }}
                    onFocus={(e) => e.currentTarget.select()}
                    onMouseDown={(e) => { e.stopPropagation(); if (document.activeElement !== e.currentTarget) { e.preventDefault(); e.currentTarget.focus(); e.currentTarget.select() } }}
                    onClick={(e) => e.stopPropagation()}
                    title={contact.hint || ''}
                    className="w-full bg-transparent border-0 text-xl leading-none text-gray-800 dark:text-gray-300 focus:bg-white dark:focus:bg-slate-800 focus:border focus:border-blue-300 focus:ring-1 focus:ring-blue-400 rounded px-1 py-0 truncate"
                  />
                </td>
                {isVis('phone') && <td data-col="phone" className="px-0.5 py-0 whitespace-nowrap text-xl leading-none text-gray-800 dark:text-gray-300 truncate text-center border-r border-gray-400 dark:border-gray-600" style={{ width: columnWidths.phone || 110, minWidth: columnWidths.phone || 110 }}>
                  {contact.phone}
                </td>}

                {/* Asignado a */}
                {isVis('assigned_to') && <td data-col="assigned_to" className="px-0.5 py-0 whitespace-nowrap overflow-hidden text-center border-r border-gray-400 dark:border-gray-600 cursor-pointer hover:bg-gray-50 dark:hover:bg-surface-dark-hover" style={{ width: columnWidths.assigned_to || 90, minWidth: columnWidths.assigned_to || 90 }}
                    onClick={(e) => {
                      if (readOnly) return
                      e.stopPropagation()
                      const rect = e.currentTarget.getBoundingClientRect()
                      if (assignSelector && assignSelector.phone === contact.phone) { setAssignSelector(null); return }
                      if (onSelectContact) onSelectContact(contact)
                      // Fetch team if not cached
                      if (teamList.length === 0) {
                        import('../../services/api').then(({ wassengerAPI }) => {
                          wassengerAPI.getTeam().then(team => {
                            const filtered = team.filter(a => {
                              const name = (a.displayName || '').toLowerCase()
                              return !['bot wassenger', 'ninguno'].some(ex => name.includes(ex))
                            })
                            setTeamList(filtered)
                            setAssignSelector({ phone: contact.phone, assigned_to: contact.assigned_to, x: rect.left, y: rect.bottom + 4 })
                          })
                        })
                      } else {
                        setAssignSelector({ phone: contact.phone, assigned_to: contact.assigned_to, x: rect.left, y: rect.bottom + 4 })
                      }
                    }}
                >
                  {contact.assigned_to && contact.assigned_to !== 'Ninguno' ? (
                    <span className="inline-flex items-center px-2 py-px rounded text-sm font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300">
                      {contact.assigned_to}
                    </span>
                  ) : (
                    <span className="text-gray-400 dark:text-gray-500 text-sm">Sin asignar</span>
                  )}
                </td>}


                {/* Etiquetas de Wassenger */}
                {isVis('labels') && <td data-col="labels" className="px-0.5 py-0 text-center border-r border-gray-400 dark:border-gray-600" style={{ width: columnWidths.labels || 50, minWidth: columnWidths.labels || 50 }}>
                  <LabelBadges labels={contact.labels || []} />
                </td>}

                {/* Campañas completadas */}
                {isVis('campaigns') && <td data-col="campaigns" className="px-0.5 py-0 text-center border-r border-gray-400 dark:border-gray-600" style={{ width: columnWidths.campaigns || 40, minWidth: columnWidths.campaigns || 40 }}>
                  <CampaignBadges campaigns={contact.completed_campaigns || []} />
                </td>}

                {/* Acciones de carpeta */}
                {isVis('folder_actions') && <td data-col="folder_actions" className="px-0.5 py-0 text-center relative border-r border-gray-400 dark:border-gray-600 last:border-r-0" style={{ width: columnWidths.folder_actions || 40, minWidth: columnWidths.folder_actions || 40 }}>
                  <div className="flex items-center justify-center gap-1">
                    {/* Botón quitar de carpeta - solo si estamos en carpeta personalizada */}
                    {currentFolderId && onRemoveFromFolder && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          onRemoveFromFolder(contact.id, currentFolderId)
                        }}
                        className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded transition"
                        title="Quitar de esta lista"
                      >
                        −
                      </button>
                    )}

                    {/* Botón agregar a carpeta */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        setShowFolderMenu(showFolderMenu === contact.id ? null : contact.id)
                      }}
                      className="p-1 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded transition"
                      title="Agregar a lista"
                    >
                      +
                    </button>
                  </div>

                  {/* Menú desplegable de carpetas */}
                  {showFolderMenu === contact.id && (
                  <>
                    <div className="fixed inset-0 z-40" onClick={(e) => { e.stopPropagation(); setShowFolderMenu(null); }} />
                    <div className="absolute right-0 top-full mt-1 w-48 bg-white dark:bg-surface-dark-alt rounded-lg shadow-lg border border-gray-200 dark:border-surface-dark-border z-50">
                      <div className="py-1 max-h-60 overflow-y-auto">
                        <div className="px-3 py-2 text-xs font-medium text-gray-500 dark:text-gray-400 border-b dark:border-slate-600">
                          Agregar a lista:
                        </div>
                        {folders.filter(f => !f.es_sistema).map((folder) => (
                          <button
                            key={folder.id}
                            onClick={(e) => {
                              e.stopPropagation()
                              if (onAddToFolder) {
                                onAddToFolder(contact.id, folder.id)
                              }
                              setShowFolderMenu(null)
                            }}
                            className="w-full px-3 py-2 text-left text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-slate-700 flex items-center gap-2"
                          >
                            <span className="text-base">{folder.icono || "📋"}</span><span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: folder.color || "#6B7280" }} />
                            {folder.nombre}
                          </button>
                        ))}
                        {folders.filter(f => !f.es_sistema).length === 0 && (
                          <div className="px-3 py-2 text-sm text-gray-500">
                            No hay listas personalizadas
                          </div>
                        )}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            const n = prompt('Nombre de la nueva lista:');
                            if (n && n.trim() && onCreateFolder) {
                              onCreateFolder(n.trim());
                            }
                            setShowFolderMenu(null);
                          }}
                          className="w-full px-3 py-2 text-left text-sm text-primary-600 dark:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20 flex items-center gap-2 border-t border-gray-200 dark:border-slate-600 font-medium"
                        >
                          <span className="text-lg">+</span>
                          <span>Crear nueva lista</span>
                        </button>
                      </div>
                    </div>
                    </>
                  )}
                </td>}
              </tr>
            )
          })}
        </tbody>
      </table>

      {/* Icon Selector Modal */}
      {iconModalContact && (
        <IconSelectorModal
          phone={iconModalContact.phone}
          contactName={iconModalContact.name}
          currentIconId={iconModalContact.iconId}
          onClose={() => setIconModalContact(null)}
          onIconChanged={iconModalContact.phone === null
            ? (iconId) => {
                setIconModalContact(null)
                if (onBulkIcon) onBulkIcon(iconId)
              }
            : (newIconId) => {
                const phoneSnapshot = iconModalContact.phone
                setIconModalContact(null)
                if (onIconChanged) onIconChanged(phoneSnapshot, newIconId)
              }
          }
        />
      )}

      {/* Description Popover (right-click on IC icon) */}
      {contextDescPopup && (() => {
        const desc = ICON_DESCRIPTIONS[contextDescPopup.iconId] || { title: contextDescPopup.iconId, body: '' }
        return (
          <DescriptionPopover
            x={contextDescPopup.x}
            y={contextDescPopup.y}
            title={desc.title}
            body={desc.body}
            onClose={() => setContextDescPopup(null)}
          />
        )
      })()}

      {/* Flag Selector Popup */}
      {flagSelector && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setFlagSelector(null)} />
          <div ref={flagMenuRef} className="fixed z-50 bg-white dark:bg-surface-dark-alt rounded-xl shadow-2xl border border-gray-200 dark:border-gray-600 py-1 min-w-[210px]" style={{ left: flagPos.left, top: flagPos.top }}>
            {flagSelector.bandera && (
              <>
                <button
                  onClick={() => {
                    if (onFlagToggle) onFlagToggle(flagSelector.phone, null)
                    import('../../services/api').then(({ prospectAPI }) => {
                      prospectAPI.updateProspect(flagSelector.phone, { bandera: null })
                    })
                    setFlagSelector(null)
                  }}
                  className="flex items-center gap-3 w-full px-4 py-1.5 text-left text-lg hover:bg-red-50 dark:hover:bg-red-900/20 text-red-500 transition"
                >
                  <span className="text-2xl">&#x2715;</span>
                  <span>Quitar flag</span>
                </button>
                <div className="border-t border-gray-200 dark:border-gray-600 my-1" />
              </>
            )}
            {FLAG_OPTIONS.map(opt => (
              <button
                key={opt.key}
                onClick={() => {
                  const newVal = flagSelector.bandera === opt.key ? null : opt.key
                  if (onFlagToggle) onFlagToggle(flagSelector.phone, newVal)
                  import('../../services/api').then(({ prospectAPI }) => {
                    prospectAPI.updateProspect(flagSelector.phone, { bandera: newVal })
                  })
                  setFlagSelector(null)
                }}
                className={`flex items-center gap-3 w-full px-4 py-1.5 text-left text-lg hover:bg-gray-100 dark:hover:bg-surface-dark-hover transition ${flagSelector.bandera === opt.key ? 'bg-blue-50 dark:bg-blue-900/30 font-semibold' : ''}`}
              >
                {opt.key === 'whatsapp' ? <svg className="w-7 h-7 text-green-500" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/><path d="M12 2C6.486 2 2 6.486 2 12c0 1.86.507 3.6 1.389 5.09L2.07 21.78a.5.5 0 00.612.638l4.857-1.3A9.95 9.95 0 0012 22c5.514 0 10-4.486 10-10S17.514 2 12 2z"/></svg> : opt.key === 'wassenger' ? <svg className="w-7 h-7 text-blue-500" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/><path d="M12 2C6.486 2 2 6.486 2 12c0 1.86.507 3.6 1.389 5.09L2.07 21.78a.5.5 0 00.612.638l4.857-1.3A9.95 9.95 0 0012 22c5.514 0 10-4.486 10-10S17.514 2 12 2z"/></svg> : opt.key === 'cotizacion_enviada' ? renderCotizacionEnviada('md') : opt.key === 'reciclado_hoy' ? <span className="text-2xl" style={{ filter: 'sepia(1) saturate(7) hue-rotate(16deg) brightness(1.08)' }}>{opt.emoji}</span> : <span className="text-2xl">{opt.emoji}</span>}
                <span>{opt.label}</span>
                {flagSelector.bandera === opt.key && <span className="ml-auto text-blue-500 text-base">&#x2713;</span>}
              </button>
            ))}
          </div>
        </>
      )}

      {/* Assign Selector Popup */}
      {assignSelector && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setAssignSelector(null)} />
          <div ref={assignMenuRef} className="fixed z-50 bg-white dark:bg-surface-dark-alt rounded-xl shadow-2xl border border-gray-200 dark:border-gray-600 py-2 min-w-[200px] max-h-[400px] overflow-y-auto" style={{ left: assignPos.left, top: assignPos.top }}>
            <div className="px-4 py-1 text-xs font-semibold text-gray-400 uppercase">Asignar a</div>
            <button
              onClick={() => {
                import('../../services/api').then(({ wassengerAPI }) => {
                  wassengerAPI.unassignAgent(assignSelector.phone).catch(() => {})
                })
                if (onAssignAgent) onAssignAgent(assignSelector.phone, null)
                setAssignSelector(null)
              }}
              className={`flex items-center gap-3 w-full px-4 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-surface-dark-hover transition ${!assignSelector.assigned_to || assignSelector.assigned_to === 'Ninguno' ? 'bg-blue-50 dark:bg-blue-900/30 font-semibold' : ''}`}
            >
              <span className="w-2.5 h-2.5 rounded-full bg-gray-300 flex-shrink-0" />
              <span>SIN ASIGNAR</span>
            </button>
            {teamList.map(agent => {
              const isActive = assignSelector.assigned_to && agent.displayName && assignSelector.assigned_to.toLowerCase() === agent.displayName.toLowerCase()
              return (
                <button
                  key={agent.id}
                  onClick={() => {
                    import('../../services/api').then(({ wassengerAPI }) => {
                      wassengerAPI.assignAgent(assignSelector.phone, agent.id).catch(() => {})
                    })
                    if (onAssignAgent) onAssignAgent(assignSelector.phone, agent.displayName)
                    setAssignSelector(null)
                  }}
                  className={`flex items-center gap-3 w-full px-4 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-surface-dark-hover transition ${isActive ? 'bg-blue-50 dark:bg-blue-900/30 font-semibold' : ''}`}
                >
                  <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: agent.color || '#6B7280' }} />
                  <span>{(agent.displayName || '').toUpperCase()}</span>
                  {isActive && <span className="ml-auto text-blue-500">&#x2713;</span>}
                </button>
              )
            })}
          </div>
        </>
      )}

      {/* Column Filter Modals (Icon/Flag) - single-select, styled like IconSelectorModal */}
      {columnFilterPopup && columnFilterPopup.type === 'icon' && (
        <IconFilterModal
          currentFilter={iconFilter}
          onSelect={(iconId) => {
            if (onIconFilterChange) onIconFilterChange(iconId)
            setColumnFilterPopup(null)
          }}
          onClose={() => setColumnFilterPopup(null)}
        
        scopeFilters={scopeFilters}
      />
      )}
      {columnFilterPopup && columnFilterPopup.type === 'flag' && (
        <FlagFilterModal
          currentFilter={flagFilter}
          onSelect={(flagId) => {
            if (onFlagFilterChange) onFlagFilterChange(flagId)
            setColumnFilterPopup(null)
          }}
          onClose={() => setColumnFilterPopup(null)}
        />
      )}

      {/* Sentinel para infinite scroll */}
      <div ref={sentinelRef} className="h-4" />

      {/* Indicador de carga */}
      {loadingMore && (
        <div className="flex items-center justify-center py-6">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600 mr-3"></div>
          <span className="text-sm text-gray-500">Cargando más contactos...</span>
        </div>
      )}

      {/* Contador de contactos */}
      {!hasMore && contacts.length > 0 && (
        <div className="text-center py-4 text-sm text-gray-400 dark:text-gray-500 border-t border-gray-100 dark:border-surface-dark-border">
          Mostrando {contacts.length} de {total} contactos
        </div>
      )}
      {hoverPopup && (
        <div
          className="fixed z-[9999] bg-white dark:bg-slate-800 border border-gray-300 dark:border-slate-600 rounded-lg shadow-2xl p-3 pointer-events-none"
          style={{ left: hoverPopup.x, top: hoverPopup.y, transform: 'translateX(-50%)', maxWidth: '680px', minWidth: '440px' }}
        >
          <div className="text-[1.4rem] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-2 border-b border-gray-200 dark:border-slate-700 pb-1">
            Últimos mensajes del prospecto
          </div>
          {hoverPopup.loading ? (
            <div className="text-[1.5rem] text-gray-400 italic">Cargando...</div>
          ) : hoverPopup.error ? (
            <div className="text-[1.5rem] text-red-500 italic">Error al cargar</div>
          ) : !hoverPopup.messages || hoverPopup.messages.length === 0 ? (
            <div className="text-[1.5rem] text-gray-400 italic">Sin mensajes recientes del cliente</div>
          ) : (
            <div className="space-y-3">
              {hoverPopup.messages.map((m) => (
                <div key={m.id} className="text-[1.5rem]">
                  <div className="text-[1.3rem] text-gray-500 dark:text-gray-400">{formatPopupTime(m.timestamp)}</div>
                  <div className="text-gray-800 dark:text-gray-200 whitespace-pre-wrap break-words">{m.text || <em className="text-gray-400">(sin texto)</em>}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
